import {
  getFirestore,
  collection,
  doc,
  addDoc,
  updateDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  Timestamp,
  serverTimestamp,
} from 'firebase/firestore';
import { firebaseApp } from '../firebase';
import {
  Ticket,
  CreateTicketData,
  UpdateTicketData,
  TicketMessage,
} from '../types/ticket';

const db = getFirestore(firebaseApp);

/**
 * Create a new support ticket
 */
export const createTicket = async (
  userId: string,
  userEmail: string,
  userName: string,
  userType: 'jobseeker' | 'hr' | 'admin',
  ticketData: CreateTicketData
): Promise<string> => {
  try {
    // Get current auth user to verify
    const { getAuth } = await import('firebase/auth');
    const auth = getAuth();
    const currentUser = auth.currentUser;

    const ticketRef = await addDoc(collection(db, 'supportTickets'), {
      userId,
      userEmail,
      userName,
      userType,
      type: ticketData.type,
      subject: ticketData.subject,
      description: ticketData.description,
      priority: ticketData.priority || 'medium',
      status: 'open',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      //attachments: ticketData.attachments || [],
      //messages: [],
    });

    return ticketRef.id;
  } catch (error: any) {
    throw error;
  }
};

/**
 * Get all tickets for a specific user
 */
export const getUserTickets = async (userId: string): Promise<Ticket[]> => {
  try {
    const q = query(
      collection(db, 'supportTickets'),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as Ticket[];
  } catch (error) {
    throw new Error('Failed to fetch tickets');
  }
};

/**
 * Get a single ticket by ID
 */
export const getTicketById = async (ticketId: string): Promise<Ticket | null> => {
  try {
    const ticketDoc = await getDoc(doc(db, 'supportTickets', ticketId));

    if (!ticketDoc.exists()) {
      return null;
    }

    return {
      id: ticketDoc.id,
      ...ticketDoc.data(),
    } as Ticket;
  } catch (error) {
    throw new Error('Failed to fetch ticket');
  }
};

/**
 * Update ticket status or other fields (Admin function)
 */
export const updateTicket = async (
  ticketId: string,
  updates: UpdateTicketData
): Promise<void> => {
  try {
    const ticketRef = doc(db, 'supportTickets', ticketId);

    const updateData: any = {
      ...updates,
      updatedAt: serverTimestamp(),
    };

    if (updates.status === 'resolved' || updates.status === 'closed') {
      updateData.resolvedAt = serverTimestamp();
    }

    await updateDoc(ticketRef, updateData);
  } catch (error) {
    throw new Error('Failed to update ticket');
  }
};

/**
 * Add a message to a ticket
 */
export const addTicketMessage = async (
  ticketId: string,
  senderId: string,
  senderName: string,
  senderType: 'user' | 'admin' | 'system',
  message: string,
  attachments?: string[]
): Promise<void> => {
  try {
    const ticketRef = doc(db, 'supportTickets', ticketId);
    const ticketDoc = await getDoc(ticketRef);

    if (!ticketDoc.exists()) {
      throw new Error('Ticket not found');
    }

    const ticketData = ticketDoc.data();
    const messages = ticketData.messages || [];

    const newMessage: TicketMessage = {
      id: `msg_${Date.now()}`,
      ticketId,
      senderId,
      senderName,
      senderType,
      message,
      createdAt: Timestamp.now(),
      attachments: attachments || [],
    };

    messages.push(newMessage);

    await updateDoc(ticketRef, {
      messages,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    throw new Error('Failed to add message to ticket');
  }
};

/**
 * Get all tickets (Admin function)
 */
export const getAllTickets = async (
  filterStatus?: string
): Promise<Ticket[]> => {
  try {
    let q;

    if (filterStatus) {
      q = query(
        collection(db, 'supportTickets'),
        where('status', '==', filterStatus),
        orderBy('createdAt', 'desc')
      );
    } else {
      q = query(
        collection(db, 'supportTickets'),
        orderBy('createdAt', 'desc')
      );
    }

    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as Ticket[];
  } catch (error) {
    throw new Error('Failed to fetch tickets');
  }
};

/**
 * Get ticket statistics
 */
export const getTicketStats = async (userId?: string) => {
  try {
    let q;

    if (userId) {
      q = query(collection(db, 'supportTickets'), where('userId', '==', userId));
    } else {
      q = query(collection(db, 'supportTickets'));
    }

    const snapshot = await getDocs(q);
    const tickets = snapshot.docs.map((doc) => doc.data());

    const stats = {
      total: tickets.length,
      open: tickets.filter((t) => t.status === 'open').length,
      inProgress: tickets.filter((t) => t.status === 'in_progress').length,
      resolved: tickets.filter((t) => t.status === 'resolved').length,
      closed: tickets.filter((t) => t.status === 'closed').length,
    };

    return stats;
  } catch (error) {
    throw new Error('Failed to fetch ticket statistics');
  }
};